import { RegistrationCommonInputProps } from 'features/registration/interface/registration';
import { getMaxDateForRegistration } from 'features/registration/utility/utility';
import { AnimatePresence, motion, useAnimation, Variants } from 'framer-motion';
import React, { FC, lazy, Suspense, useEffect } from 'react';
import Checkbox from 'shared/components/form/checkbox';
import FormRow from 'shared/components/form/formRow';
import InputSelect from 'shared/components/form/inputSelect';
import InputText from 'shared/components/form/inputText';
import { COUNTRY_CODES } from 'shared/constants/common';
import useInputFocus from 'shared/hooks/useInputFocus';
import { disableInputAlphabets } from 'shared/util/utility';
import './styles.scss';

const InputDatePicker = lazy(() => import('shared/components/form/inputDatePicker'));

interface Props extends RegistrationCommonInputProps {
	setFieldError: (fieldName: string, value: string) => void;
	handleBlur: (e: React.FocusEvent<any>) => void;
	setFieldTouched: (field: string, isTouched?: boolean, shouldValidate?: boolean) => void;
}

const RegistrationStep5: FC<Props> = ({
	t,
	values,
	isFieldValueValid,
	touched,
	errors,
	setFieldValue,
	setFieldError,
	handleBlur,
	setFieldTouched,
	setValues = () => {},
}) => {
	const inputRef = useInputFocus();

	const variants: Variants = {
		visible: { x: 0, opacity: 1 },
		hidden: { x: 0, opacity: 0, zIndex: 0 },
	};
	const controls = useAnimation();
	useEffect(() => {
		controls.start(values.peselNumNotAvailable ? 'hidden' : 'visible');
	}, [values.peselNumNotAvailable]);

	return (
		<>
			<AnimatePresence>
				{!values.peselNumNotAvailable && (
					<motion.div initial="hidden" variants={variants} animate={controls}>
						<FormRow>
							<InputText
								autoFocus
								inputRef={inputRef}
								label={t('peselNum')}
								name={'peselNum'}
								isFieldValueValid={isFieldValueValid}
								errors={errors}
								touched={touched}
								onBlur={handleBlur}
								setFieldValue={setFieldValue}
								values={values}
								onChange={(e) => setFieldValue('peselNum', disableInputAlphabets(e))}
								inputProps={{ maxLength: 11, inputMode: 'numeric' }}
								cyTestRef="pesel-number"
								cyErrorRef="reg-validation-error"
							/>
						</FormRow>
					</motion.div>
				)}
			</AnimatePresence>
			<FormRow>
				<motion.div
					initial={{ position: 'relative', top: '0px', marginTop: '8px' }}
					animate={{ position: 'relative', top: values.peselNumNotAvailable ? '-18px' : '0px' }}
					transition={{ duration: 0.4, ease: 'easeOut' }}
				>
					<Checkbox
						name="peselNumNotAvailable"
						label={t('peselNotAvailable')}
						values={values}
						isFieldValueValid={isFieldValueValid}
						touched={touched}
						errors={errors}
						setFieldValue={setFieldValue}
						onChange={(e: any) => {
							setValues({
								...values,
								peselNumNotAvailable: e.target.checked,
								peselNum: '',
								dob: '',
								country: '',
							});
							setFieldError('dob', '');
							setFieldError('country', '');
							setFieldError('peselNum', '');
						}}
						cyTestRef="pesel-number-checkbox"
					/>
				</motion.div>
			</FormRow>
			{values.peselNumNotAvailable && (
				<>
					<FormRow>
						<Suspense fallback={<></>}>
							<InputDatePicker
								name="dob"
								id="dob"
								label={t('dob')}
								touched={touched}
								maxDate={getMaxDateForRegistration}
								isFieldValueValid={isFieldValueValid}
								setFieldValue={setFieldValue}
								setFieldError={setFieldError}
								errors={errors}
								values={values}
								handleBlur={handleBlur}
								onChange={(date: any) => {
									if (values.peselNumNotAvailable) {
										setFieldValue('dob', date);
									}
								}}
								cyTestRef="birthday"
								cyErrorRef="reg-validation-error"
							/>
						</Suspense>
					</FormRow>
					<FormRow>
						<InputSelect
							label={t('placeholderChooseCountry')}
							errors={errors}
							touched={touched}
							name="country"
							values={values}
							selectItems={COUNTRY_CODES}
							isFieldValueValid={isFieldValueValid}
							setFieldValue={setFieldValue}
							setFieldTouched={setFieldTouched}
							setFieldError={setFieldError}
							onBlur={handleBlur}
							className="RegistrationStep5__countrySelection"
							placeholder={t('placeholderChooseCountry')}
							cyTestRef="country"
							cyErrorRef="reg-validation-error"
						/>
					</FormRow>
				</>
			)}
		</>
	);
};
export default RegistrationStep5;
